import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import useAsyncEffect from 'use-async-effect';
import moment from 'moment';
import ReactECharts from 'echarts-for-react';
import { Link } from 'react-router-dom';
import { FiDownload, FiFileText, FiPlay, FiTrendingUp, FiBarChart2, FiArrowRight, FiChevronDown } from 'react-icons/fi';
import { IGlobalState } from '../../../model/IGlobalState';
import { HoldingsMovementReportSummaryView as Summary } from '../../../model/HoldingsMovementReportResults';
import { RegisterListingResult } from '../../../model/RegisterListingResult';
import api from '../../../api/api';
import { to } from '../../../util/util';
import { providers, registerAsAt, pageSizes, pageList } from './AllHolders';
import { ranges, compact } from '../Dashboard';

const periods: Record<string, number> = { 'Last 30 Days': 30, 'Last 90 Days': 91, 'Last 12 Months': 365, 'All': Infinity };

const previousBusinessDay = () => {
    const d = moment().subtract(1, 'day');
    while (d.isoWeekday() > 5) d.subtract(1, 'day');
    return d.format('YYYY-MM-DD');
};

const aud = (v?: number | null) => v == null ? '—' : `A$${Math.round(v).toLocaleString()}`;
const signed = (n: number) => `${n > 0 ? '+' : n < 0 ? '−' : ''}${Math.abs(n).toLocaleString()}`;
const day = (r: Summary) => moment(r.date).format('YYYY-MM-DD');

const Badges: React.FC<{ r: Summary }> = ({ r }) => <>
    {r.newCount > 0 && <span className="badge new">{r.newCount} New</span>}
    {r.existingCount > 0 && <span className="badge existing">{r.existingCount} Existing</span>}
    {r.returningCount > 0 && <span className="badge returning">{r.returningCount} Returning</span>}
</>;

const MovementChart: React.FC<{ rows: Summary[] }> = ({ rows }) => {
    const option = {
        animation: false,
        grid: { left: 44, right: 24, top: 12, bottom: 28 },
        tooltip: {
            trigger: 'axis',
            backgroundColor: '#0b1739', borderWidth: 0, padding: [8, 12],
            textStyle: { color: '#fff', fontSize: 12 },
            axisPointer: { type: 'line', lineStyle: { color: '#0f172a', type: 'dashed' } },
            formatter: (params: any[]) => {
                const r = rows[params[0].dataIndex];
                return `<strong>${moment(r.date).format('D MMM')} · ${compact(r.totalAbsMovement)}</strong><br/><span style="color:#94a3b8;font-size:11px">${r.movementCount} movement${r.movementCount === 1 ? '' : 's'}</span>`;
            },
        },
        xAxis: {
            type: 'category', boundaryGap: false, data: rows.map(day),
            axisLine: { show: false }, axisTick: { show: false },
            axisLabel: { color: '#5f6b7d', fontSize: 12, formatter: (d: string) => moment(d).format('D MMM') },
        },
        yAxis: {
            type: 'value',
            splitLine: { lineStyle: { color: '#e5e9f0', type: 'dashed' } },
            axisLabel: { color: '#5f6b7d', fontSize: 12, formatter: (v: number) => compact(v) },
        },
        series: [{
            type: 'line', showSymbol: false, data: rows.map(r => r.totalAbsMovement),
            lineStyle: { color: '#00B5D1', width: 2.5 }, itemStyle: { color: '#00B5D1' },
            areaStyle: { color: { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: 'rgba(0,181,209,0.35)' }, { offset: 1, color: 'rgba(0,181,209,0)' }] } },
        }],
    };
    return <ReactECharts option={option} notMerge={true} style={{ height: 210 }} />;
};

const HoldingsMovement: React.FC = () => {
    const stock = useSelector((state: IGlobalState) => state.currentStock);
    const [reports, setReports] = useState<Summary[] | null>(null);
    const [listing, setListing] = useState<RegisterListingResult | null>(null);
    const [error, setError] = useState('');
    const [reportDate, setReportDate] = useState(previousBusinessDay());
    const [running, setRunning] = useState(false);
    const [runMessage, setRunMessage] = useState('');
    const [range, setRange] = useState('1M');
    const [period, setPeriod] = useState('Last 30 Days');
    const [pageSize, setPageSize] = useState(pageSizes[0]);
    const [page, setPage] = useState(1);

    const loadReports = async () => {
        if (!stock) return;
        const [err, data] = await to(api().getHoldingsMovementReportSummaries(stock.ticker, stock.exchange));
        if (err || !data.success) { setError(err?.message || data?.message || 'Failed to fetch movement reports'); return; }
        setError('');
        setReports(data.reports);
    };

    useAsyncEffect(async (isMounted: () => boolean) => {
        if (!stock) return;
        setReports(null);
        // the listing carries the as-at time and the holder names for Largest moves
        const [, [, reg]] = await Promise.all([loadReports(), to(api().getRegisterListing(stock.ticker, stock.exchange))]);
        if (isMounted()) setListing(reg?.success ? reg : null);
    }, [stock?.id]);

    const names = useMemo(() => new Map((listing?.holders || []).map(h => [h.holderId, h.name])), [listing]);
    // the v2 detail screen (MovementReportDetail.tsx), which reads the v1 report page
    const reportUrl = (r: Summary) => `/share-registry/holdings-movement/${day(r)}`;

    // reports come newest first
    const latest = reports?.[0];
    const since = moment().subtract(ranges[range], 'days').format('YYYY-MM-DD');
    const chartRows = (reports || []).filter(r => day(r) >= since).reverse();
    const cutoff = periods[period] === Infinity ? '' : moment().subtract(periods[period], 'days').format('YYYY-MM-DD');
    const filtered = (reports || []).filter(r => day(r) >= cutoff);
    const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
    const current = Math.min(page, pages);
    const rows = filtered.slice((current - 1) * pageSize, current * pageSize);
    const latestIsYesterday = latest && day(latest) === previousBusinessDay();
    const provider = providers[stock?.shareRegistryProvider ?? 0] ?? 'registry';
    const asAt = registerAsAt(listing?.asAt);
    const minValue = stock?.shareRegistryOptions?.newHolderMinValue ?? 0;

    const run = async () => {
        if (!stock) return;
        setRunning(true);
        setRunMessage('');
        const [err, result] = await to(api().runHoldingsMovementReport(stock.ticker, stock.exchange, reportDate));
        setRunning(false);
        if (err || !result.success) { setRunMessage(err?.message || result?.message || 'Failed to run the report'); return; }
        setRunMessage(`${moment(reportDate).format('D MMM YYYY')}: ${result.movementCount} movement${result.movementCount === 1 ? '' : 's'}, ${result.totalAbsMovement.toLocaleString()} shares moved.`);
        await loadReports();
    };

    const download = () => {
        const cell = (v: string | number) => `"${String(v).replace(/"/g, '""')}"`;
        const lines = [['Date', 'Movements', 'New', 'Existing', 'Returning', 'Shares moved', 'Est. value (A$)'],
            ...filtered.map(r => [day(r), r.movementCount, r.newCount, r.existingCount, r.returningCount, r.totalAbsMovement, r.estValue == null ? '' : Math.round(r.estValue)])];
        const a = document.createElement('a');
        a.href = URL.createObjectURL(new Blob([lines.map(l => l.map(cell).join(',')).join('\r\n')], { type: 'text/csv' }));
        a.download = `${stock?.ticker}-holdings-movement.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
    };

    return <div className="holdings-movement">
        <div className="page-header">
            <div>
                <h1>Share Registry</h1>
                <p>{[stock?.companyName, provider !== 'None' && provider, asAt && `Register as at ${asAt}`].filter(Boolean).join(' · ')}</p>
            </div>
            <button type="button" className="btn-outline" data-tour="movement-download" onClick={download} disabled={!filtered.length}><FiDownload /> Download Report</button>
        </div>

        {error && <p className="error">{error}</p>}
        {!error && !reports && <p className="loading">Loading movement reports…</p>}

        {reports && <>
            <div className="layout" data-tour="movement-run">
                <section className="card run">
                    <div className="title"><span className="icon-tile"><FiFileText /></span>
                        <div><h2>Run a Movement Report</h2><p>Compares the chosen day's register with the previous trading day</p></div></div>
                    <label className="field-label" htmlFor="hm-report-date">Report date</label>
                    <div className="run-row">
                        <input id="hm-report-date" type="date" value={reportDate} max={moment().format('YYYY-MM-DD')} onChange={e => setReportDate(e.target.value)} />
                        <button type="button" className="btn-primary" onClick={run} disabled={running || !reportDate}><FiPlay /> {running ? 'Running…' : 'Run Report'}</button>
                        <div className="ranges">{Object.keys(ranges).map(r => <button type="button" key={r} className={r === range ? 'active' : ''} onClick={() => setRange(r)}>{r}</button>)}</div>
                    </div>
                    {runMessage && <p className="run-message">{runMessage}</p>}
                    {chartRows.length > 1 ? <MovementChart rows={chartRows} /> : <p className="sub">Not enough reports in this range for a chart</p>}
                    <div className="legend">
                        <span>Badges:</span>
                        <span className="badge new">New</span><span>first-time holder</span>
                        <span className="badge existing">Existing</span><span>increased holding</span>
                        <span className="badge returning">Returning</span><span>previously sold out</span>
                        {minValue > 0 && <span>· only holdings over A${minValue.toLocaleString()}</span>}
                    </div>
                </section>

                <section className="card glance">
                    <div className="title"><span className="icon-tile"><FiTrendingUp /></span>
                        <div><h2>{latestIsYesterday ? 'Yesterday' : 'Latest Report'} at a Glance</h2><p>{latest ? moment(latest.date).format('dddd, D MMMM YYYY') : 'No reports yet'}</p></div></div>
                    {latest && <>
                        <div className="tiles">
                            <div className="tile"><span className="k">Movements</span><span className="v">{latest.movementCount.toLocaleString()}</span></div>
                            <div className="tile"><span className="k">Shares moved</span><span className="v">{latest.totalAbsMovement.toLocaleString()}</span></div>
                            <div className="tile"><span className="k">Est. value</span><span className="v">{aud(latest.estValue)}</span></div>
                            <div className="tile"><span className="k">New holders</span><span className="v">{latest.newCount}</span></div>
                        </div>
                        <h3>Largest moves</h3>
                        {(latest.largestMoves || []).map(m => <div className="move" key={m.holderId}>
                            <span className="name">{names.get(m.holderId) ?? `Holder ${m.holderId}`}</span>
                            <span className={`pill ${m.netMovement >= 0 ? 'ok' : 'bad'}`}>{signed(m.netMovement)}</span>
                        </div>)}
                        {!latest.largestMoves && <p className="sub">Shown for reports run from 1 October 2026.</p>}
                        <Link className="link open" to={reportUrl(latest)}>Open {latestIsYesterday ? "Yesterday's" : 'Latest'} Report <FiArrowRight /></Link>
                    </>}
                </section>
            </div>

            <section className="card daily" data-tour="movement-daily">
                <div className="daily-header">
                    <div className="title"><span className="icon-tile"><FiBarChart2 /></span>
                        <div><h2>Daily Movement Reports</h2><p>One row per trading day · click a row to open the full report</p></div></div>
                    <label className="filter">
                        <select aria-label="Period" value={period} onChange={e => { setPeriod(e.target.value); setPage(1); }}>{Object.keys(periods).map(p => <option key={p} value={p}>{p}</option>)}</select>
                        <FiChevronDown />
                    </label>
                </div>
                <div className="table">
                    <div className="row head"><span>Date</span><span>Movements</span><span className="num">Shares moved</span><span className="num">Est. value</span><span></span></div>
                    {rows.map(r => <Link className="row" key={day(r)} to={reportUrl(r)}>
                        <span><strong>{moment(r.date).format('D MMM YYYY')}</strong><span className="sub">{moment(r.date).format('dddd')}</span></span>
                        <span className="movements"><strong>{r.movementCount}</strong><Badges r={r} /></span>
                        <span className="num">{r.totalAbsMovement.toLocaleString()}</span>
                        <span className="num value">{aud(r.estValue)}</span>
                        <span className="open">Open report <FiArrowRight /></span>
                    </Link>)}
                    {rows.length === 0 && <p className="empty">No reports in this period</p>}
                </div>
                <div className="footer">
                    <span className="count">Showing {rows.length} of {filtered.length} trading day{filtered.length === 1 ? '' : 's'}</span>
                    <label className="filter rows">
                        <select aria-label="Rows per page" value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}>{pageSizes.map(n => <option key={n} value={n}>Rows per page: {n}</option>)}</select>
                        <FiChevronDown />
                    </label>
                    <div className="pagination">
                        <button type="button" disabled={current === 1} onClick={() => setPage(current - 1)}>‹</button>
                        {pageList(pages, current).map((p, i) => p === '…' ? <span key={`e${i}`}>…</span>
                            : <button type="button" key={p} className={p === current ? 'active' : ''} onClick={() => setPage(p)}>{p}</button>)}
                        <button type="button" disabled={current === pages} onClick={() => setPage(current + 1)}>›</button>
                    </div>
                </div>
            </section>
        </>}
    </div>;
};

export default HoldingsMovement;
